import { useContext, useState } from "react";
import { ScrollView, StyleSheet, Text, View } from "react-native";
import Icon from "react-native-vector-icons/MaterialCommunityIcons";
import Button from "../../components/Button";
import PageTitle from "../../components/PageTitle";
import TextBoxIcon from "../../components/TextBoxIcon";
import { GlobalContext } from "../../contexts/global";
import { adjustWalletBalance } from "../../services/Api/admin";
import * as Colors from "../../styles/colors";
import * as Fonts from "../../styles/typography";

export default function AdminWalletAdjust({ navigation, route }) {
  const { userId, userName, currentBalance } = route.params;
  const { setLoading, setShowAlert, showAlert } = useContext(GlobalContext);

  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [result, setResult] = useState(null);
  const [amountValid, setAmountValid] = useState(true);
  const [reasonValid, setReasonValid] = useState(true);

  const validateAmount = (value) => {
    if (!value || value.trim() === "") {
      return false;
    }
    const numValue = parseFloat(value.replace(",", "."));
    return !isNaN(numValue) && numValue !== 0;
  };

  const validateReason = (value) => {
    return value && value.trim().length >= 10;
  };

  const handleAmountChange = (value) => {
    // Allow numbers, minus sign, comma and dot
    const sanitized = value.replace(/[^0-9,.\-]/g, "");
    setAmount(sanitized);
    setAmountValid(true); // Reset validation on change
  };

  const handleReasonChange = (value) => {
    setReason(value);
    setReasonValid(true); // Reset validation on change
  };

  const handleSubmit = () => {
    // Validate inputs
    const isAmountValid = validateAmount(amount);
    const isReasonValid = validateReason(reason);

    setAmountValid(isAmountValid);
    setReasonValid(isReasonValid);

    if (!isAmountValid || !isReasonValid) {
      return;
    }

    const numericAmount = parseFloat(amount.replace(",", "."));

    setLoading({ show: true, message: "Processando ajuste" });

    adjustWalletBalance(userId, numericAmount, reason.trim())
      .then((response) => {
        setResult(response);
        setShowAlert({
          type: "success",
          title: "Sucesso",
          message: "Ajuste realizado com sucesso",
          show: true,
          confirmText: "OK",
          pressConfirm: () => setShowAlert({ ...showAlert, show: false }),
        });
      })
      .catch((error) => {
        const message =
          error?.message || error?.erro || "Erro ao realizar ajuste";
        setShowAlert({
          type: "alert",
          title: "Erro",
          message: message,
          show: true,
          confirmText: "OK",
          pressConfirm: () => setShowAlert({ ...showAlert, show: false }),
        });
      })
      .finally(() => {
        setLoading({ show: false, message: "" });
      });
  };

  const handleNewAdjustment = () => {
    setAmount("");
    setReason("");
    setResult(null);
    setAmountValid(true);
    setReasonValid(true);
  };

  const formatCurrency = (value) => {
    return `R$ ${(value || 0).toFixed(2)}`;
  };

  const getAmountColor = (value) => {
    if (value > 0) return Colors.SUCCESS;
    if (value < 0) return Colors.ALERT;
    return Colors.BLACK;
  };

  return (
    <PageTitle title={"Ajustar Carteira"} navigation={navigation}>
      <ScrollView style={styles.container}>
        {/* User Info Header */}
        <View style={styles.userHeader}>
          <Icon name="account-circle" size={48} color={Colors.PRIMARY} />
          <View style={styles.userInfo}>
            <Text style={styles.userName}>{userName}</Text>
          </View>
        </View>

        {/* Current Balance */}
        <View style={styles.balanceSection}>
          <View style={styles.balanceHeader}>
            <Icon name="wallet" size={24} color={Colors.WALLET} />
            <Text style={styles.balanceLabel}>Saldo Atual</Text>
          </View>
          <Text style={styles.balanceValue}>
            {formatCurrency(result ? result.newBalance : currentBalance)}
          </Text>
        </View>

        {/* Result Display (after successful adjustment) */}
        {result && (
          <View style={styles.resultSection}>
            <Text style={styles.sectionTitle}>Resultado do Ajuste</Text>
            
            <View style={styles.resultRow}>
              <Text style={styles.resultLabel}>Saldo Anterior:</Text>
              <Text style={styles.resultValue}>
                {formatCurrency(result.previousBalance)}
              </Text>
            </View>
            
            <View style={styles.resultRow}>
              <Text style={styles.resultLabel}>Valor do Ajuste:</Text>
              <Text
                style={[
                  styles.resultValue,
                  { color: getAmountColor(result.adjustmentAmount) },
                ]}
              >
                {result.adjustmentAmount > 0 ? "+" : ""}
                {formatCurrency(result.adjustmentAmount)}
              </Text>
            </View>
            
            <View style={styles.resultRow}>
              <Text style={styles.resultLabel}>Novo Saldo:</Text>
              <Text style={[styles.resultValue, styles.newBalance]}>
                {formatCurrency(result.newBalance)}
              </Text>
            </View>

            <View style={styles.resultRow}>
              <Text style={styles.resultLabel}>Motivo:</Text>
              <Text style={styles.resultReason}>{result.reason}</Text>
            </View>

            <Button
              textoBotao="Novo Ajuste"
              estilo={styles.newAdjustButton}
              onPress={handleNewAdjustment}
            />
          </View>
        )}

        {/* Input Form (hidden after successful adjustment) */}
        {!result && (
          <View style={styles.formSection}>
            <Text style={styles.sectionTitle}>Dados do Ajuste</Text>

            {/* Amount Input */}
            <Text style={styles.inputLabel}>
              Valor (positivo para crédito, negativo para débito)
            </Text>
            <TextBoxIcon
              iconName="cash"
              placeholderInput="Ex: 50.00 ou -25.50"
              value={amount}
              onChangeTextInput={handleAmountChange}
              keyboardType="numeric"
              isValid={amountValid}
              invalidMessage="Digite um valor válido diferente de zero"
            />

            {/* Reason Input */}
            <Text style={styles.inputLabel}>
              Motivo (mínimo 10 caracteres)
            </Text>
            <TextBoxIcon
              iconName="text-box-outline"
              placeholderInput="Descreva o motivo do ajuste"
              value={reason}
              onChangeTextInput={handleReasonChange}
              maxLength={500}
              isValid={reasonValid}
              invalidMessage="O motivo deve ter pelo menos 10 caracteres"
            />

            {/* Character count */}
            <Text style={styles.charCount}>
              {reason.length}/500 caracteres (mínimo: 10)
            </Text>

            {/* Info Box */}
            <View style={styles.infoBox}>
              <Icon name="information-outline" size={20} color={Colors.INFO} />
              <Text style={styles.infoText}>
                Use valores positivos para adicionar crédito e valores negativos
                para remover saldo da carteira do usuário.
              </Text>
            </View>

            {/* Submit Button */}
            <Button
              textoBotao="Confirmar Ajuste"
              estilo={styles.submitButton}
              onPress={handleSubmit}
            />
          </View>
        )}
      </ScrollView>
    </PageTitle>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.WHITE,
  },
  userHeader: {
    flexDirection: "row",
    alignItems: "center",
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: Colors.GRAY_LIGHT,
  },
  userInfo: {
    marginLeft: 12,
  },
  userName: {
    fontSize: Fonts.FONT_SIZE_18,
    fontWeight: "bold",
    color: Colors.BLACK,
  },
  balanceSection: {
    padding: 16,
    backgroundColor: Colors.SNOW,
    marginHorizontal: 16,
    marginVertical: 16,
    borderRadius: 8,
    alignItems: "center",
  },
  balanceHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
  balanceLabel: {
    fontSize: Fonts.FONT_SIZE_16,
    color: Colors.WALLET,
    marginLeft: 8,
    fontWeight: "bold",
  },
  balanceValue: {
    fontSize: Fonts.FONT_SIZE_28,
    color: Colors.WALLET,
    fontWeight: "bold",
  },
  formSection: {
    padding: 16,
  },
  sectionTitle: {
    fontSize: Fonts.FONT_SIZE_16,
    fontWeight: "bold",
    color: Colors.BLACK,
    marginBottom: 16,
  },
  inputLabel: {
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.GRAY_DARK,
    marginBottom: 8,
  },
  charCount: {
    fontSize: Fonts.FONT_SIZE_12,
    color: Colors.GRAY_DARK,
    textAlign: "right",
    marginTop: -12,
    marginBottom: 16,
  },
  submitButton: {
    backgroundColor: Colors.SUCCESS,
    marginTop: 8,
    marginBottom: 16,
  },
  infoBox: {
    flexDirection: "row",
    backgroundColor: Colors.SNOW,
    padding: 12,
    borderRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: Colors.INFO,
  },
  infoText: {
    flex: 1,
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.GRAY_DARK,
    marginLeft: 8,
  },
  resultSection: {
    padding: 16,
    backgroundColor: Colors.SNOW,
    marginHorizontal: 16,
    marginBottom: 16,
    borderRadius: 8,
  },
  resultRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: Colors.GRAY_LIGHT,
  },
  resultLabel: {
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.GRAY_DARK,
  },
  resultValue: {
    fontSize: Fonts.FONT_SIZE_16,
    fontWeight: "bold",
    color: Colors.BLACK,
  },
  resultReason: {
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.BLACK,
    flex: 1,
    textAlign: "right",
    marginLeft: 16,
  },
  newBalance: {
    color: Colors.WALLET,
    fontSize: Fonts.FONT_SIZE_18,
  },
  newAdjustButton: {
    backgroundColor: Colors.PRIMARY,
    marginTop: 16,
  },
});
